import {
  Box,
  Button,
  List,
  ListItemAvatar,
  ListItemButton,
  ListItemText,
  Stack,
  Typography,
} from '@mui/material'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Fade, IconButton, Icons } from 'utoniq-ui'

import { GpsData, Maybe } from '@/clients/utoniq-core/schema'
import { ConsoleBaseLayout } from '@/components/layouts/ConsoleBaseLayout'
import {
  FullscreenModal,
  GpsMap,
  GpsMapProps,
} from '@/components/organisms/functionals'
import { useTranslation } from '@/core'
import { Cell, CellFrame } from '@/core/architecture'
import { Place, Prediction } from '@/lib/googleMap'

import { AutocompleteForm, AutocompleteFormProps } from './AutocompleteForm'

export type GPSMapModalProps = GpsMapProps & {
  artistId: string
  open: boolean
  onClose: () => void
  onSearch: AutocompleteFormProps['onSubmit']
  predictions: Prediction[]
  place: Maybe<Place>
  onSelectPlace: (values: {
    placeId: string
    map: google.maps.Map
  }) => Promise<void>
  onSpecify: (values: {
    latitude: number
    longitude: number
    formattedAddress?: string
  }) => void
}

export const GPSMapModal: Cell<GPSMapModalProps> = ({
  artistId,
  open,
  onClose,
  onSearch,
  predictions,
  place,
  onSelectPlace,
  onSpecify,
  currentData,
}) => {
  const { t } = useTranslation('consoleMissionCreate')
  const gpsMapRef = useRef<google.maps.Map>()
  const [currentStep, setCurrentStep] = useState<number>(0)
  const [selectedPrediction, setSelectedPrediction] = useState<Prediction>()

  const pinData = useMemo<GpsData | undefined>(() => {
    if (!place?.location.lat || !place?.location.lng) {
      return undefined
    }
    return {
      latitude: place.location.lat,
      longitude: place.location.lng,
    }
  }, [place?.location])

  const handleMapLoad = useCallback(
    (mapInstance?: google.maps.Map) => {
      if (place?.location?.lat && place?.location?.lng) {
        mapInstance?.panTo(
          new google.maps.LatLng(place.location.lat, place.location.lng)
        )
        return
      }
      if (currentData?.latitude && currentData?.longitude) {
        mapInstance?.panTo(
          new google.maps.LatLng(currentData.latitude, currentData.longitude)
        )
      }
    },
    [currentData, place]
  )

  useEffect(() => {
    handleMapLoad(gpsMapRef.current)
  }, [open, handleMapLoad])

  const handleClick = useCallback(
    async (event: google.maps.MapMouseEvent & { placeId?: string }) => {
      if (!event.placeId || !gpsMapRef.current) {
        return
      }
      setSelectedPrediction(undefined)
      await onSelectPlace({
        placeId: event.placeId,
        map: gpsMapRef.current,
      })
    },
    [onSelectPlace]
  )

  return (
    <FullscreenModal open={open} sx={{ zIndex: 3 }}>
      <ConsoleBaseLayout
        sx={{ height: '100dvh' }}
        header={{
          left: (
            <IconButton.Back
              edge="start"
              size="medium"
              sx={{
                '.MuiSvgIcon-root svg': {
                  color: 'white',
                },
              }}
              onClick={() => {
                onClose()
                setCurrentStep(0)
              }}
            />
          ),
          title: t('positionRaisePin'),
        }}
      >
        <Stack
          sx={{
            height: '100%',
            display: currentStep === 0 ? 'flex' : 'none',
            transition: 'display 300ms ease-in-out',
          }}
        >
          <Stack flex={1} sx={{ position: 'relative' }}>
            <GpsMap
              currentData={currentData}
              onLoad={map => {
                gpsMapRef.current = map
                handleMapLoad(map)
              }}
              pinData={pinData}
              onClick={handleClick}
            />
            <Stack
              sx={{ position: 'absolute', top: '32px', right: 0, left: 0 }}
              onClick={() => setCurrentStep(1)}
              alignItems="center"
            >
              <Button
                color="inherit"
                startIcon={<Icons.Search sx={{ color: 'grey.700' }} />}
                sx={{ width: 335, height: 40, bgcolor: 'grey.50' }}
              >
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ width: '100%', pl: 2 }}
                  align="left"
                >
                  {t('search')}
                </Typography>
              </Button>
            </Stack>
          </Stack>
          {(selectedPrediction || place) && (
            <Fade>
              <AddressAreaCell
                prediction={selectedPrediction}
                place={place}
                onSpecify={() =>
                  onSpecify({
                    latitude: place?.location.lat ?? 0,
                    longitude: place?.location.lng ?? 0,
                    formattedAddress: place?.formattedAddress ?? '',
                  })
                }
              />
            </Fade>
          )}
        </Stack>
        <Stack
          sx={{
            height: '100%',
            display: currentStep === 1 ? 'flex' : 'none',
            transition: 'display 300ms ease-in-out',
          }}
        >
          <AutocompleteForm
            onSubmit={onSearch}
            onGoBack={() => setCurrentStep(0)}
          />
          <SearchAddressListCell
            predictions={predictions}
            onSelect={async prediction => {
              if (!gpsMapRef.current) {
                return
              }
              setSelectedPrediction(undefined)
              await onSelectPlace({
                placeId: prediction.placeId,
                map: gpsMapRef.current,
              })
              setSelectedPrediction(prediction)
              setCurrentStep(0)
            }}
          />
        </Stack>
      </ConsoleBaseLayout>
    </FullscreenModal>
  )
}

type AddressAreaCellProps = {
  prediction?: Prediction
  place: Maybe<Place>
  onSpecify: () => void
}
const AddressAreaCell: Cell<AddressAreaCellProps> = ({
  prediction,
  place,
  onSpecify,
}) => {
  const { t } = useTranslation('consoleMissionCreate')
  const addressTitle = prediction ? prediction.mainText : place?.name
  const addressDesc = prediction
    ? prediction.secondaryText
    : place?.formattedAddress
  return (
    <CellFrame sx={{ p: 5 }}>
      <Typography variant="body1">{addressTitle}</Typography>
      <Typography variant="caption" color="text.secondary">
        {addressDesc}
      </Typography>
      <Stack alignItems="center" sx={{ pt: 4 }}>
        <Button size="large" sx={{ width: 244 }} onClick={onSpecify}>
          {t('specify')}
        </Button>
      </Stack>
    </CellFrame>
  )
}

type SearchAddressListCellProps = {
  predictions: Prediction[]
  onSelect: (prediction: Prediction) => void
}
const SearchAddressListCell: Cell<SearchAddressListCellProps> = ({
  predictions,
  onSelect,
}) => {
  return (
    <CellFrame sx={{ px: 5 }}>
      <List>
        {predictions.map(prediction => (
          <ListItemButton
            component={Box}
            key={prediction.placeId}
            divider
            onClick={() => onSelect(prediction)}
          >
            <ListItemAvatar>
              <Icons.Pin />
            </ListItemAvatar>
            <ListItemText
              primary={prediction.mainText}
              secondary={prediction.secondaryText}
              slotProps={{
                primary: { variant: 'body2' },
                secondary: { variant: 'caption', color: 'text.secondary' },
              }}
            />
          </ListItemButton>
        ))}
      </List>
    </CellFrame>
  )
}
